import React, { useLayoutEffect, useRef, useState } from "react";
import TierBadge from "../../v2/components/TierBadge";
import { tierOption } from "../../v2/tierMeta";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import shareTourLink from "../shareTourLink";
import GroundTourTierModal from "./GroundTourTierModal";

const ICONS = "/iceholidays-assets/images/icons";

interface Props {
  landTour: LandTourDetail;
}

export default function GroundTourHeader({ landTour }: Props) {
  const isMobile = useBreakpoint();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);

  useLayoutEffect(() => {
    if (!isMobile || expanded) return;
    const el = titleRef.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [isMobile, expanded, landTour.title]);

  const tierTag = tierOption(landTour.brand) && (
    // Opens the "Tour Tier" popup (issue #792 item 2) explaining what each tier promises.
    <button
      type="button"
      className="ground-tour-show-v2__tier-tag"
      aria-haspopup="dialog"
      aria-label={`Tour tier ${tierOption(landTour.brand)?.tier}: see what each tier includes`}
      onClick={() => setTierOpen(true)}
    >
      <TierBadge tier={landTour.brand} />
      {/* Masked so it recolors via currentColor to match .tier-tag's own color. */}
      <span
        className="ground-tour-show-v2__tier-tag-chevron"
        style={{
          WebkitMaskImage: `url(${ICONS}/icon-tier-chevron.svg)`,
          maskImage: `url(${ICONS}/icon-tier-chevron.svg)`,
        }}
        aria-hidden="true"
      />
    </button>
  );

  const tierModal = tierOption(landTour.brand) && (
    <GroundTourTierModal open={tierOpen} onClose={() => setTierOpen(false)} tier={landTour.brand} />
  );

  const codeChip = (
    <span className="ground-tour-show-v2__code-chip">
      <img src={`${ICONS}/icon-tour-code.png`} alt="" aria-hidden="true" />
      {landTour.code}
    </span>
  );

  if (isMobile) {
    return (
      <header className="ground-tour-show-v2__header ground-tour-show-v2__header--mobile">
        {landTour.isNew && <span className="ground-tour-show-v2__new-badge">New</span>}

        <div className="ground-tour-show-v2__header-tags">
          {tierTag}
          {codeChip}
        </div>

        <div className="ground-tour-show-v2__title-block">
          <h1 ref={titleRef} className={`ground-tour-show-v2__title ${expanded ? "is-expanded" : ""}`}>
            {landTour.title}
          </h1>

          {(clamped || expanded) && (
            <button type="button" className="ground-tour-show-v2__read-more" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Read Less" : "Read More"}
            </button>
          )}
        </div>

        {tierModal}
      </header>
    );
  }

  return (
    <header className="ground-tour-show-v2__header">
      <div className="ground-tour-show-v2__header-chips">
        {landTour.isNew && <span className="ground-tour-show-v2__new-badge">New</span>}
        {codeChip}
      </div>

      <div className="ground-tour-show-v2__title-row">
        <h1 className="ground-tour-show-v2__title">{landTour.title}</h1>

        <div className="ground-tour-show-v2__title-actions">
          {tierTag}

          <button type="button" className="ground-tour-show-v2__share" onClick={shareTourLink}>
            <span className="ground-tour-show-v2__share-icon">
              <img src={`${ICONS}/icon-share.png`} alt="" aria-hidden="true" />
            </span>
            Share
          </button>
        </div>
      </div>

      {tierModal}
    </header>
  );
}
